import { useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from "recharts";
import {
  TrendingUp, Briefcase, CheckCircle, Users, ShieldCheck, AlertTriangle,
  Download, Car, Shield,
} from "lucide-react";
import { useDashboardDetails } from "../../../hooks/api/dashboard/hooks";
import {
  useJobsPerPeriod, useRevenuePerPeriod, useTechUtilisation,
  useFleetReport, useDbsReport, useRtwReport,
} from "../../../hooks/api/reports/hooks";
import { DatePicker, Skeleton, Button, Tabs, Tag, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IVehicle } from "../../../types/api/vehicle";
import { IDbsCheck, IRtwCheck } from "../../../hooks/api/compliance/callbacks";
import { SEMANTIC_HEX } from "../../../constants/statusColors";

dayjs.extend(quarterOfYear);

const INVOICE_COLORS: Record<string, string> = {
  paid: SEMANTIC_HEX.success,
  outstanding: SEMANTIC_HEX.warning,
  overdue: SEMANTIC_HEX.danger,
  draft: SEMANTIC_HEX.neutral,
};

// ─── helpers ─────────────────────────────────────────────────────────────────

const daysUntilDate = (d: string | null): number | null =>
  d ? dayjs(d).diff(dayjs(), "day") : null;

type MotStatus = "ok" | "due_soon" | "overdue" | "unknown";
const motStatus = (days: number | null): MotStatus => {
  if (days === null) return "unknown";
  if (days < 0) return "overdue";
  if (days <= 30) return "due_soon";
  return "ok";
};

type ComplianceStatus = "ok" | "expiring_soon" | "expired" | "unknown";
const complianceStatus = (days: number | null): ComplianceStatus => {
  if (days === null) return "unknown";
  if (days < 0) return "expired";
  if (days <= 90) return "expiring_soon";
  return "ok";
};

const MOT_BADGE: Record<MotStatus, { label: string; color: string }> = {
  ok: { label: "OK", color: "success" },
  due_soon: { label: "Due Soon", color: "warning" },
  overdue: { label: "Overdue", color: "error" },
  unknown: { label: "No Date", color: "default" },
};

const COMP_BADGE: Record<ComplianceStatus, { label: string; color: string }> = {
  ok: { label: "OK", color: "success" },
  expiring_soon: { label: "Expiring Soon", color: "warning" },
  expired: { label: "Expired", color: "error" },
  unknown: { label: "No Date", color: "default" },
};

// ─── CSV helpers ─────────────────────────────────────────────────────────────

function downloadCSV(rows: (string | number | null)[][], filename: string) {
  const csv = rows
    .map((r) =>
      r
        .map((c) => {
          let cell = String(c ?? "");
          // Neutralise CSV/formula injection: a client/business name that
          // starts with =, +, -, @ (or a tab/CR) is interpreted as a
          // formula by Excel/Sheets when the file is opened, which can be
          // used to execute commands or leak data. Prefixing with a single
          // quote forces it to be read as plain text without changing the
          // visible value.
          if (/^[=+\-@\t\r]/.test(cell)) cell = `'${cell}`;
          return `"${cell.replace(/"/g, '""')}"`;
        })
        .join(",")
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

const OverviewTab = ({
  dateRange,
  setDateRange,
}: {
  dateRange: [Dayjs, Dayjs];
  setDateRange: (r: [Dayjs, Dayjs]) => void;
}) => {
  const from = dateRange[0].format("YYYY-MM-DD");
  const to = dateRange[1].format("YYYY-MM-DD");

  const { data: dashData, isLoading: dashLoading } = useDashboardDetails();
  const { data: revenueData, isLoading: revLoading } = useRevenuePerPeriod(from, to);
  const { data: jobsData, isLoading: jobsLoading } = useJobsPerPeriod(from, to);
  const { data: techData, isLoading: techLoading } = useTechUtilisation(from, to);

  const dashboard = dashData?.data;
  const revenueRows = revenueData?.data ?? [];
  const jobRows = jobsData?.data ?? [];
  const techRows = techData?.data ?? [];

  // "Collected" must sum amount_paid (r.collected), not the invoice total
  // (r.revenue) — a partially_paid invoice's unpaid balance would otherwise
  // be counted as cash in hand, overstating what the owner has actually
  // received (see reports/controller.ts's revenuePerPeriod, which already
  // computes both figures separately).
  const ytdRevenue = revenueRows.reduce((s, r) => s + Number(r.collected ?? 0), 0);

  const isCurrentYear =
    dateRange[0].isSame(dayjs().startOf("year"), "day") &&
    dateRange[1].isSame(dayjs().endOf("year"), "day");
  const revenueLabel = isCurrentYear
    ? "YTD Revenue"
    : `Revenue (${dateRange[0].format("D MMM")} – ${dateRange[1].format("D MMM YYYY")})`;
  const totalJobs = dashboard?.jobs?.total ?? 0;
  const completedJobs = dashboard?.jobs?.completed ?? 0;
  const activeWorkers = dashboard?.workers?.active ?? 0;

  const revenueChartData = revenueRows.map((r) => ({ month: r.period, revenue: Number(r.revenue ?? 0) }));
  const jobsChartData = jobRows.map((r) => ({ period: r.period, total: Number(r.total_jobs ?? 0), completed: Number(r.completed ?? 0) }));
  const invoicePieData = [
    { name: "Paid", value: Number(dashboard?.invoices?.total_paid ?? 0), color: INVOICE_COLORS.paid },
    { name: "Outstanding", value: Number(dashboard?.invoices?.outstanding ?? 0), color: INVOICE_COLORS.outstanding },
    { name: "Overdue", value: Number(dashboard?.invoices?.overdue_count ?? 0), color: INVOICE_COLORS.overdue },
  ].filter((d) => d.value > 0);

  const KPI_CARDS = [
    { label: revenueLabel, value: `£${ytdRevenue.toLocaleString("en-GB")}`, sub: isCurrentYear ? "Collected this year" : "Collected in period", icon: TrendingUp, grad: "from-blue-500 to-blue-700" },
    { label: "Total Jobs", value: totalJobs.toString(), sub: `${completedJobs} completed`, icon: Briefcase, grad: "from-indigo-500 to-indigo-700" },
    { label: "Completion Rate", value: totalJobs > 0 ? `${Math.round((completedJobs / totalJobs) * 100)}%` : "—", sub: "Jobs completed this year", icon: CheckCircle, grad: "from-emerald-500 to-emerald-700" },
    { label: "Active Workers", value: activeWorkers.toString(), sub: `of ${dashboard?.workers?.total ?? 0} total`, icon: Users, grad: "from-amber-400 to-amber-600" },
  ];

  const isLoading = dashLoading || revLoading || jobsLoading || techLoading;

  const exportCSV = () => {
    const completedByPeriod = new Map(jobRows.map((r) => [r.period, Number(r.completed ?? 0)]));
    const rows: (string | number)[][] = [
      ["Month", "Revenue", "Jobs Completed"],
      ...revenueRows.map((r) => [r.period, r.revenue ?? 0, completedByPeriod.get(r.period) ?? 0]),
    ];
    downloadCSV(rows, `report-${from}-to-${to}.csv`);
  };

  return (
    <>
      {/* header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Reports & Analytics</h1>
          <p className="text-slate-500 text-sm">Analytics for the selected date range</p>
        </div>
        <div className="flex items-center gap-3">
          <Button icon={<Download size={14} />} onClick={exportCSV}>Export CSV</Button>
          <span className="text-sm font-medium text-slate-600">Date range:</span>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => { if (dates?.[0] && dates?.[1]) setDateRange([dates[0], dates[1]]); }}
            format="DD MMM YYYY"
            allowClear={false}
            presets={[
              { label: "This month", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
              { label: "Last month", value: [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
              { label: "This quarter", value: [dayjs().startOf("quarter"), dayjs().endOf("quarter")] },
              { label: "This year", value: [dayjs().startOf("year"), dayjs().endOf("year")] },
              { label: "Last year", value: [dayjs().subtract(1, "year").startOf("year"), dayjs().subtract(1, "year").endOf("year")] },
            ]}
          />
        </div>
      </div>

      {/* KPI */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => <Skeleton.Button key={i} active block style={{ height: 96, borderRadius: 12 }} />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {KPI_CARDS.map(({ label, value, sub, icon: Icon, grad }) => (
            <div key={label} className={`bg-gradient-to-br ${grad} rounded-xl p-4 text-white`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium opacity-90">{label}</div>
                <Icon size={20} className="opacity-75" />
              </div>
              <div className="text-2xl font-bold">{value}</div>
              <div className="text-xs opacity-75 mt-0.5">{sub}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-2 xl:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 text-sm mb-4">Revenue by Month</h2>
          {revLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v: any) => [`£${Number(v).toLocaleString("en-GB")}`, ""]} contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #e2e8f0" }} />
                <Line type="monotone" dataKey="revenue" stroke="var(--color-primary)" strokeWidth={2.5} dot={{ r: 4, fill: "var(--color-primary)" }} name="Revenue" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 text-sm mb-4">Invoice Collection</h2>
          {dashLoading ? <Skeleton active paragraph={{ rows: 3 }} /> : (
            <>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={invoicePieData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={3} dataKey="value">
                    {invoicePieData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => [`£${Number(v).toLocaleString("en-GB")}`, ""]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {invoicePieData.map(({ name, value, color }) => (
                  <div key={name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-slate-600">{name}</span>
                    </div>
                    <span className="font-medium text-slate-700">£{value.toLocaleString("en-GB")}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-6">
        <div className="md:col-span-2 xl:col-span-2 bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <h2 className="font-semibold text-slate-800 text-sm mb-4">Jobs by Month</h2>
          {jobsLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={jobsChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12, border: "1px solid #e2e8f0" }} />
                <Bar dataKey="total" fill="#e2e8f0" radius={[4, 4, 0, 0]} name="Total" barSize={14} />
                <Bar dataKey="completed" fill="var(--color-primary)" radius={[4, 4, 0, 0]} name="Completed" barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck size={16} style={{ color: "var(--color-primary)" }} />
            <h2 className="font-semibold text-slate-800 text-sm">Workforce</h2>
          </div>
          {dashLoading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-emerald-500" />
                  <span className="text-xs text-slate-600">Active workers</span>
                </div>
                <span className="text-xs font-semibold text-emerald-600">{dashboard?.workers?.active ?? 0}/{dashboard?.workers?.total ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-emerald-500" />
                  <span className="text-xs text-slate-600">Jobs completed</span>
                </div>
                <span className="text-xs font-semibold text-emerald-600">{completedJobs}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-amber-500" />
                  <span className="text-xs text-slate-600">Jobs in progress</span>
                </div>
                <span className="text-xs font-semibold text-amber-600">{dashboard?.jobs?.in_progress ?? 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={14} className="text-red-500" />
                  <span className="text-xs text-slate-600">Invoices overdue</span>
                </div>
                <span className="text-xs font-semibold text-red-600">{dashboard?.invoices?.overdue_count ?? 0}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* technician utilisation */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5">
        <h2 className="font-semibold text-slate-800 text-sm mb-4">Technician Utilisation</h2>
        {techLoading ? <Skeleton active paragraph={{ rows: 5 }} /> : techRows.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">No data for this period</p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-4">
            {techRows.map((w) => {
              const initials = (w.tech_name ?? "?").split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();
              return (
                <div key={w.assigned_user_id} className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                    {initials}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{w.tech_name ?? "Unknown"}</span>
                      <span className="text-xs text-slate-500">{w.total_jobs} jobs · {w.completed_jobs} done</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full" style={{ width: `${Math.min(w.utilisation_pct ?? 0, 100)}%`, background: "var(--color-primary)" }} />
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {w.utilisation_pct === null ? "No scheduled time" : `${w.utilisation_pct}% utilisation`}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Fleet Tab ────────────────────────────────────────────────────────────────

const FleetTab = () => {
  const { data, isLoading } = useFleetReport();
  const vehicles: IVehicle[] = data?.data ?? [];

  // attach computed fields and sort by mot_due ascending (nulls last)
  const rows = vehicles
    .map((v) => {
      const days = daysUntilDate(v.mot_due);
      const status = motStatus(days);
      return { ...v, _motDays: days, _motStatus: status };
    })
    .sort((a, b) => {
      if (a._motDays === null && b._motDays === null) return 0;
      if (a._motDays === null) return 1;
      if (b._motDays === null) return -1;
      return a._motDays - b._motDays;
    });

  const columns: ColumnsType<(typeof rows)[0]> = [
    {
      title: "Reg Plate",
      dataIndex: "registration",
      key: "registration",
      render: (v) => <span className="font-mono font-semibold text-slate-800">{v ?? "—"}</span>,
    },
    { title: "Make", dataIndex: "make", key: "make", render: (v) => v ?? "—" },
    { title: "Model", dataIndex: "model", key: "model", render: (v) => v ?? "—" },
    {
      title: "Assigned Engineer",
      dataIndex: "assigned_user_name",
      key: "assigned_user_name",
      render: (v) => v ?? <span className="text-slate-400">Unassigned</span>,
    },
    {
      title: "MOT Due",
      dataIndex: "mot_due",
      key: "mot_due",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY") : <span className="text-slate-400">—</span>,
    },
    {
      title: "Service Due",
      dataIndex: "service_due",
      key: "service_due",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY") : <span className="text-slate-400">—</span>,
    },
    {
      title: "Days Until MOT",
      dataIndex: "_motDays",
      key: "_motDays",
      render: (v: number | null) => {
        if (v === null) return <span className="text-slate-400">—</span>;
        if (v < 0) return <span className="text-red-600 font-semibold">{Math.abs(v)}d overdue</span>;
        return <span className={v <= 30 ? "text-amber-600 font-semibold" : "text-slate-700"}>{v}d</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "_motStatus",
      key: "_motStatus",
      render: (s: MotStatus) => {
        const b = MOT_BADGE[s];
        return <Tag color={b.color}>{b.label}</Tag>;
      },
    },
  ];

  const exportFleetCSV = () => {
    const header = ["Reg Plate", "Make", "Model", "Assigned Engineer", "MOT Due", "Service Due", "Days Until MOT", "Status"];
    const dataRows = rows.map((r) => [
      r.registration ?? "",
      r.make ?? "",
      r.model ?? "",
      r.assigned_user_name ?? "Unassigned",
      r.mot_due ? dayjs(r.mot_due).format("DD/MM/YYYY") : "",
      r.service_due ? dayjs(r.service_due).format("DD/MM/YYYY") : "",
      r._motDays !== null ? String(r._motDays) : "",
      MOT_BADGE[r._motStatus].label,
    ]);
    downloadCSV([header, ...dataRows], `fleet-mot-report-${dayjs().format("YYYY-MM-DD")}.csv`);
  };

  // summary cards
  const overdue = rows.filter((r) => r._motStatus === "overdue").length;
  const dueSoon = rows.filter((r) => r._motStatus === "due_soon").length;
  const ok = rows.filter((r) => r._motStatus === "ok").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Car size={18} className="text-blue-600" /> Fleet / MOT Report
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">Vehicle MOT and service status, sorted by urgency</p>
        </div>
        <Button icon={<Download size={14} />} onClick={exportFleetCSV} disabled={rows.length === 0}>
          Export CSV
        </Button>
      </div>

      {/* summary pills */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{overdue}</div>
          <div className="text-xs text-red-500 mt-0.5">Overdue</div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{dueSoon}</div>
          <div className="text-xs text-amber-500 mt-0.5">Due within 30 days</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{ok}</div>
          <div className="text-xs text-emerald-500 mt-0.5">OK</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        {isLoading ? (
          <div className="p-6"><Skeleton active paragraph={{ rows: 6 }} /></div>
        ) : (
          <Table
            columns={columns}
            dataSource={rows.map((r, i) => ({ ...r, key: r.id ?? i }))}
            pagination={false}
            size="small"
            locale={{ emptyText: "No vehicles found" }}
            rowClassName={(r) =>
              r._motStatus === "overdue"
                ? "bg-red-50"
                : r._motStatus === "due_soon"
                ? "bg-amber-50"
                : ""
            }
          />
        )}
      </div>
    </div>
  );
};

// ─── Compliance Tab ───────────────────────────────────────────────────────────

type ComplianceRow = {
  key: string;
  worker_name: string | null;
  check_type: string;
  expiry_date: string | null;
  days: number | null;
  status: ComplianceStatus;
};

const ComplianceTab = () => {
  const { data: dbsData, isLoading: dbsLoading } = useDbsReport();
  const { data: rtwData, isLoading: rtwLoading } = useRtwReport();

  const dbsChecks: IDbsCheck[] = dbsData?.data ?? [];
  const rtwChecks: IRtwCheck[] = rtwData?.data ?? [];

  // Build unified compliance rows — show all, sorted by expiry asc
  const allRows: ComplianceRow[] = [
    ...dbsChecks.map((d) => {
      const days = daysUntilDate(d.expiry_date);
      return {
        key: `dbs-${d.id}`,
        worker_name: d.worker_name ?? null,
        check_type: `DBS (${d.check_level.replace(/_/g, " ")})`,
        expiry_date: d.expiry_date,
        days,
        status: complianceStatus(days),
      };
    }),
    ...rtwChecks.map((r) => {
      const days = daysUntilDate(r.expiry_date);
      return {
        key: `rtw-${r.id}`,
        worker_name: r.worker_name ?? null,
        check_type: `RTW (${r.document_type.replace(/_/g, " ")})`,
        expiry_date: r.expiry_date,
        days,
        status: complianceStatus(days),
      };
    }),
  ].sort((a, b) => {
    if (a.days === null && b.days === null) return 0;
    if (a.days === null) return 1;
    if (b.days === null) return -1;
    return a.days - b.days;
  });

  const columns: ColumnsType<ComplianceRow> = [
    {
      title: "Worker Name",
      dataIndex: "worker_name",
      key: "worker_name",
      render: (v) => v ?? <span className="text-slate-400">Unknown</span>,
    },
    {
      title: "Check Type",
      dataIndex: "check_type",
      key: "check_type",
      render: (v) => <span className="capitalize">{v}</span>,
    },
    {
      title: "Expiry Date",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY") : <span className="text-slate-400">No expiry</span>,
    },
    {
      title: "Days Remaining",
      dataIndex: "days",
      key: "days",
      render: (v: number | null) => {
        if (v === null) return <span className="text-slate-400">—</span>;
        if (v < 0) return <span className="text-red-600 font-semibold">{Math.abs(v)}d expired</span>;
        if (v <= 90) return <span className="text-amber-600 font-semibold">{v}d</span>;
        return <span className="text-slate-700">{v}d</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: ComplianceStatus) => {
        const b = COMP_BADGE[s];
        return <Tag color={b.color}>{b.label}</Tag>;
      },
    },
  ];

  const exportComplianceCSV = () => {
    const header = ["Worker Name", "Check Type", "Expiry Date", "Days Remaining", "Status"];
    const dataRows = allRows.map((r) => [
      r.worker_name ?? "Unknown",
      r.check_type,
      r.expiry_date ? dayjs(r.expiry_date).format("DD/MM/YYYY") : "",
      r.days !== null ? String(r.days) : "",
      COMP_BADGE[r.status].label,
    ]);
    downloadCSV([header, ...dataRows], `compliance-expiry-report-${dayjs().format("YYYY-MM-DD")}.csv`);
  };

  const expired = allRows.filter((r) => r.status === "expired").length;
  const expiringSoon = allRows.filter((r) => r.status === "expiring_soon").length;
  const ok = allRows.filter((r) => r.status === "ok").length;

  const isLoading = dbsLoading || rtwLoading;

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Shield size={18} className="text-emerald-600" /> Compliance Expiry Report
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">DBS and Right-to-Work checks expiring within 90 days or already expired</p>
        </div>
        <Button icon={<Download size={14} />} onClick={exportComplianceCSV} disabled={allRows.length === 0}>
          Export CSV
        </Button>
      </div>

      {/* summary pills */}
      <div className="grid grid-cols-3 gap-4 mb-5">
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{expired}</div>
          <div className="text-xs text-red-500 mt-0.5">Expired</div>
        </div>
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{expiringSoon}</div>
          <div className="text-xs text-amber-500 mt-0.5">Expiring within 90 days</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{ok}</div>
          <div className="text-xs text-emerald-500 mt-0.5">OK</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-100 shadow-sm">
        {isLoading ? (
          <div className="p-6"><Skeleton active paragraph={{ rows: 6 }} /></div>
        ) : (
          <Table
            columns={columns}
            dataSource={allRows}
            pagination={false}
            size="small"
            locale={{ emptyText: "No compliance records found" }}
            rowClassName={(r) =>
              r.status === "expired"
                ? "bg-red-50"
                : r.status === "expiring_soon"
                ? "bg-amber-50"
                : ""
            }
          />
        )}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const ReportsPage = () => {
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("year"),
    dayjs().endOf("year"),
  ]);

  const tabItems = [
    {
      key: "overview",
      label: (
        <span className="flex items-center gap-1.5">
          <TrendingUp size={14} /> Overview
        </span>
      ),
      children: <OverviewTab dateRange={dateRange} setDateRange={setDateRange} />,
    },
    {
      key: "fleet",
      label: (
        <span className="flex items-center gap-1.5">
          <Car size={14} /> Fleet / MOT
        </span>
      ),
      children: <FleetTab />,
    },
    {
      key: "compliance",
      label: (
        <span className="flex items-center gap-1.5">
          <Shield size={14} /> Compliance
        </span>
      ),
      children: <ComplianceTab />,
    },
  ];

  return (
    <div className="p-6 min-h-screen">
      <Tabs
        defaultActiveKey="overview"
        items={tabItems}
        size="large"
        className="reports-tabs"
      />
    </div>
  );
};

export default ReportsPage;
